import { useRef } from "react";
import { Link } from "react-router-dom";
import { useInView, motion } from "framer-motion";
import { Lock, Compass, Eye, Puzzle, ArrowRight } from "lucide-react";
import { Seo } from "../lib/seo";
import { IMAGES } from "../lib/site";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "../components/motion";
import { SectionHeading } from "../components/bits";
import { PageHero } from "../components/PageHero";

const TENETS = [
  { icon: Lock, t: "Confidentiality", d: "Client information is protected without exception. What is shared in a consultation stays within the four walls of the engagement." },
  { icon: Compass, t: "Diligence", d: "Advice is only as good as the work behind it. We verify records, read the fine print and never advise past the evidence." },
  { icon: Eye, t: "Transparency", d: "Scope, fees and prospects are stated plainly, in advance. You should never have to ask where your matter stands." },
  { icon: Puzzle, t: "Practical solutions", d: "The best legal answer is the one you can implement. We weigh enforceability against cost, time and commercial common sense." },
];

const ProfilePlaceholder = () => (
  <div className="card-line flex flex-col items-start gap-6 p-9 sm:flex-row sm:items-center" data-testid="profile-placeholder">
    <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border border-dashed border-gold/60 bg-cream">
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-dark text-center leading-relaxed">
        Add
        <br />
        Photo
      </span>
    </div>
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">Professional profile — editable placeholder</p>
      <h3 className="mt-2 font-serif text-2xl text-ink">[Advocate / Founder name]</h3>
      <p className="mt-2 text-sm leading-relaxed text-steel">
        [Qualifications, enrolment details and areas of specialisation to be
        added here — entered only once verified. This profile deliberately
        contains no invented credentials, years of experience or affiliations.]
      </p>
    </div>
  </div>
);

export const About = () => {
  const imgRef = useRef(null);
  const inView = useInView(imgRef, { once: true, amount: 0.3 });
  return (
    <>
      <Seo
        title="About Us"
        description="Upgrade Law — a modern Indian legal advisory practice built on confidentiality, diligence, transparency and practical solutions for businesses and individuals."
        path="/about"
      />
      <PageHero
        eyebrow="About Us"
        title={["A practice built on", "judgment, not theatre."]}
        description="Upgrade Law is a legal advisory practice serving businesses, startups, entrepreneurs and individuals across India. We keep the craft old-fashioned — careful, confidential, honest — and the delivery modern."
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Philosophy & Approach"
              title="Legal advice should change what you do next."
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-steel">
                Too much legal work is produced to be filed away rather than
                used. Our approach inverts that: every opinion, agreement or
                report we prepare is written so that the person receiving it
                can act on it — decide, sign, file, negotiate or settle.
              </p>
              <p className="mt-4 text-base leading-relaxed text-steel">
                That means advising within the evidence, flagging risk in
                plain language, and saying clearly when a matter does not need
                a lawyer at all. It is a slower way to build a practice and a
                better way to keep one.
              </p>
              <p className="mt-4 text-base leading-relaxed text-steel">
                The practice is aware of, and works within, the professional
                and ethical standards that govern legal services in India,
                including the Advocates Act, 1961 and the rules of the Bar
                Council of India. This website is informational and
                non-promotional by design.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div ref={imgRef} className="relative">
                <div className="absolute -left-5 -top-5 h-full w-full border border-gold/50" aria-hidden="true" />
                <motion.img
                  src={IMAGES.handshake}
                  alt="A professional advisory meeting"
                  initial={{ clipPath: "inset(0 0 0 100%)" }}
                  animate={inView ? { clipPath: "inset(0 0 0 0%)" } : {}}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  className="relative aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-canvas py-24 sm:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="What we hold to"
            title="Four commitments, applied to every matter."
            align="center"
          />
          <Stagger className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TENETS.map((t, i) => (
              <StaggerItem key={t.t} className="h-full">
                <div className="group h-full border border-line bg-white p-8 transition-all duration-500 hover:border-gold/60 hover:shadow-[0_24px_60px_-30px_rgba(11,21,40,0.35)]">
                  <t.icon size={24} strokeWidth={1.5} className="text-gold-dark" />
                  <h3 className="mt-6 font-serif text-xl text-ink">{t.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-steel">{t.d}</p>
                  <span className="mt-6 block font-mono text-[10px] text-steel/40">0{i + 1} / 04</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Experience & Expertise"
              title="Where the practice works."
              description="The practice advises across corporate and commercial law, intellectual property and trademarks, contract drafting and management, real estate and property transactions, regulatory compliance, and dispute resolution. Details of experience are stated only as they can be verified — placeholders remain until then."
            />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-3">
                {["Corporate & Commercial Law", "Intellectual Property & Trademark", "Contract Drafting & Management", "Real Estate & Property Law", "Regulatory & Legal Compliance", "Litigation & Dispute Resolution"].map((a, i) => (
                  <div key={a} className="flex items-baseline gap-4 border-b border-line pb-3">
                    <span className="font-mono text-[11px] text-gold-dark">0{i + 1}</span>
                    <span className="text-sm font-medium text-ink">{a}</span>
                  </div>
                ))}
              </div>
              <Link to="/services" className="btn-outline mt-8">
                Explore services <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <ProfilePlaceholder />
              <div className="mt-6 border-l-2 border-gold bg-cream p-7">
                <p className="text-sm leading-relaxed text-steel">
                  <span className="font-semibold text-ink">A note on accuracy:</span>{" "}
                  in keeping with professional standards, this page contains no
                  invented qualifications, client names, case results or
                  statistics. Placeholders in square brackets are editable and
                  should be completed only with verifiable details before launch.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
